import { httpClient } from '@/shared/api/httpClient'
import type { DirectPaymentResponse } from '@/modules/payment/types/directPayment'
import type {
  WalletBalanceApiResponse,
  WalletHistoryApiResponse,
  WalletHistoryRequest,
  WalletRatesApiResponse,
  WithdrawalApiResponse,
  WithdrawalPayload,
  SendFundsPayload,
  WithdrawalV2Payload,
  WithdrawalV2Response,
  ExchangePayload,
  ExchangeResponse,
  ExchangeRateResponse,
  ExchangeRateInfo,
} from '@/modules/wallet/types/wallet'

interface ApiResponse<T> {
  result: string
  data: T
}

const toFormData = (params: WalletHistoryRequest = {}) => {
  const formData = new FormData()

  if (params.page) {
    formData.append('page', String(params.page))
  }

  if (params.date) {
    formData.append('date', params.date)
  }

  if (params.type && params.type !== 'all') {
    formData.append('type', String(params.type))
  }

  if (params.walletId && params.walletId !== 'all') {
    formData.append('wallet_id', String(params.walletId))
  }

  if (params.status && params.status !== 'all') {
    formData.append('status', String(params.status))
  }

  if (params.currency && params.currency !== 'all') {
    formData.append('currency', params.currency)
  }

  return formData
}

export const fetchWalletBalance = async () => {
  const response = await httpClient.get<ApiResponse<WalletBalanceApiResponse>>('/wallet/balance')
  return response.data
}

export const fetchWalletHistory = async (params: WalletHistoryRequest = {}) => {
  const body = toFormData({
    page: params.page ?? 1,
    date: params.date,
    type: params.type,
    walletId: params.walletId,
    status: params.status,
    currency: params.currency,
  })

  const response = await httpClient.post<ApiResponse<WalletHistoryApiResponse>>('/wallet/history', body, {
    headers: {},
  })

  return response.data
}

export const fetchWalletRates = async () => {
  const response = await httpClient.get<ApiResponse<WalletRatesApiResponse>>('/wallet/exchange-rates')
  return response.data
}

export const postWithdrawalRequest = async (params: WithdrawalPayload) => {
  const payload: any = {
    currency: params.currency,
    amount: params.amount,
    address: params.address,
    paypass: params.paypass,
    form_id: 'walletWithdraw', // Yandex-required security flag
  }

  if (params.wallet_id) {
    payload.wallet_id = params.wallet_id
  }

  if (params.confirmAction) {
    payload.confirmAction = params.confirmAction
  }

  if (params.confirmActionCode) {
    payload.confirmActionCode = params.confirmActionCode
  }

  const response = await httpClient.post<WithdrawalApiResponse>('/wallet/withdraw', payload)
  return response.data
}

/**
 * Новый метод вывода V2 с выбором сети
 * Адрес берется ТОЛЬКО из профиля пользователя
 */
export const postWithdrawalV2Request = async (params: WithdrawalV2Payload): Promise<WithdrawalV2Response> => {
  const payload: Record<string, any> = {
    currency: params.currency,
    amount: params.amount,
    network: params.network,
    form_id: 'walletWithdrawV2',
  }

  if (params.target_token) {
    payload.target_token = params.target_token
  }

  if (params.paypass) {
    payload.paypass = params.paypass
  }

  if (params.confirmAction) {
    payload.confirmAction = params.confirmAction
  }

  if (params.confirmActionCode) {
    payload.confirmActionCode = params.confirmActionCode
  }

  const response = await httpClient.post<{ data: WithdrawalV2Response }>('/wallet/withdraw-v2', payload)
  return response.data ?? { success: false, message: 'No response data' }
}

export const postSendFundsRequest = async (params: SendFundsPayload) => {
  const payload: any = {
    send_to_id: params.userId,
    currency: params.currency,
    amount: params.amount,
  }

  if (params.paypass) {
    payload.paypass = params.paypass
  }

  if (params.confirmAction) {
    payload.confirmAction = params.confirmAction
  }

  if (params.confirmActionCode) {
    payload.confirmActionCode = params.confirmActionCode
  }

  const response = await httpClient.post<WithdrawalApiResponse>('/wallet/send', payload)
  return response
}

// Balance check for payment options
export interface BalanceCheckPayload {
  amount: number
  currency?: string
}

export interface AvailableCurrency {
  symbol: string
  /** GNK is returned as an exact decimal string by UserWallet::amount(). */
  balance: number | string
  is_sufficient: boolean
}

export interface BalanceCheckResponse {
  success: boolean
  can_pay: boolean
  recommended_currency: string | null
  available_currencies: AvailableCurrency[]
  amount?: number
  currency?: string
  error?: string
  message?: string
}

/**
 * Check if user has sufficient balance for wallet payment.
 * Backend checks USD and all stablecoins (USDT, USDC, BUSD, DAI).
 */
export const checkBalance = async (params: BalanceCheckPayload): Promise<BalanceCheckResponse> => {
  try {
    const response = await httpClient.post<{ data: BalanceCheckResponse }>('/wallet/check-balance', {
      amount: params.amount,
      currency: params.currency ?? 'USD',
    })
    return response.data ?? {
      success: false,
      can_pay: false,
      recommended_currency: null,
      available_currencies: [],
      message: 'No response data',
    }
  } catch (error) {
    console.error('[GonkaGame][wallet] balance check error', error)
    return {
      success: false,
      can_pay: false,
      recommended_currency: null,
      available_currencies: [],
      error: 'request_failed',
      message: error instanceof Error ? error.message : 'Failed to check balance',
    }
  }
}

export const postCancelTransaction = async (id: number): Promise<{ result: number }> => {
  const payload = new FormData()
  payload.append('id', String(id))
  const response = await httpClient.post<{ result: number }>('/wallet/transfer/cancel', payload)
  return response
}

/**
 * Current dynamic exchange rate for the USDT ⇄ GNK pair
 */
export const fetchExchangeRate = async (from: string, to: string): Promise<ExchangeRateResponse> => {
  const raw = await httpClient.get<unknown>(
    `/wallet/exchange-rate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  )
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('EXCHANGE_INVALID_RESPONSE')
  const envelope = raw as Record<string, unknown>
  if ('result' in envelope && envelope.result !== 'success') throw new Error('EXCHANGE_INVALID_RESPONSE')
  const response = 'success' in envelope ? envelope : envelope.data
  if (!response || typeof response !== 'object' || Array.isArray(response)
    || typeof (response as Record<string, unknown>).success !== 'boolean') throw new Error('EXCHANGE_INVALID_RESPONSE')
  const payload = response as Record<string, unknown>
  // This endpoint uses an explicit boolean, not transport/JSMessage success.
  if (payload.success === false) {
    const message = payload.message
    return {
      success: false,
      error: typeof payload.error === 'string' && payload.error !== '' ? payload.error : 'unknown_error',
      message: typeof message === 'string' ? message
        : message && typeof message === 'object' && 'text' in message && typeof message.text === 'string' ? message.text : '',
    }
  }
  const data = payload.data as ExchangeRateInfo | undefined
  const decimal = (value: unknown, signed = false): value is string => typeof value === 'string'
    && (signed ? /^-?(0|[1-9]\d*)(?:\.\d+)?$/ : /^(0|[1-9]\d*)(?:\.\d+)?$/).test(value)
  if (!data || data.from !== from || data.to !== to || !decimal(data.rate) || !/[1-9]/.test(data.rate)
    || !decimal(data.base_price_usd) || !decimal(data.adjustment_percent, true) || !decimal(data.fee_percent)) {
    throw new Error('EXCHANGE_INVALID_RESPONSE')
  }
  return { success: true, data }
}

/**
 * Exchange currency (USDT ⇄ GNK — dynamic rate; USD ⇄ stablecoins — 1:1)
 */
export const postExchangeRequest = async (params: ExchangePayload): Promise<ExchangeResponse> => {
  const response = await httpClient.post<ExchangeResponse>('/wallet/exchange', {
    from_currency: params.from_currency,
    to_currency: params.to_currency,
    amount: params.amount,
    expected_rate: params.expected_rate,
  })
  return response
}

export interface TopupPayload {
  amount: number
  currency: string
  payment_method: 'gonka' | 'onchainpay'
  crypto_network?: string
  crypto_token?: string
}

/**
 * Wallet top-up via DirectPaymentService (replaces the legacy
 * topup-gonka/topup-crypto form relay). Returns the DirectPaymentResponse
 * union rendered inline by OnchainPaymentWidget.
 */
export const postTopupRequest = async (params: TopupPayload): Promise<DirectPaymentResponse> => {
  return httpClient.post<DirectPaymentResponse>('/wallet/topup', params)
}

/** DePay has its own string-amount request and managed-widget response. */
export const postDePayTopupRequest = (amount: string, clientRequestId: string, signal?: AbortSignal) =>
  httpClient.post<unknown>('/wallet/topup', {
    payment_method: 'depay', currency: 'USDT', amount, client_request_id: clientRequestId,
  }, { signal })
